import { Divider, Flex, FlexProps, Stack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { zeroAddress } from "viem";
import { useRouter } from "next/router";
import {
  LinkIcon,
  IconProps,
  LevelsFilledIcon,
  LogoutIcon,
  NextLink,
  Text,
  SettingsFillIcon,
  Chevron,
  GlobeIcon,
  Button,
  TicketRedeemablesFillIcon,
  ProfileAvatar,
} from "@looksrare/uikit";
import { formatAddress, formatCompactNumber, useIsBlastApp } from "@looksrare/utils";
import { ProfileNavProps } from "@/components/ProfileNav";
import { useDisconnectWallet, useUserSeasonPoints } from "@/hooks";
import { languageList } from "@/config/localization";
import { useUser } from "@/queries/user";
import { ProfileRakebackRow } from "./ProfileRakebackRow";

interface ProfileViewProps extends ProfileNavProps {
  onClickLanguage: () => void;
}

interface ProfileViewLinkProps extends FlexProps {
  leftIcon: (iconProps: IconProps) => JSX.Element;
  title: string;
  rightElement?: React.ReactElement;
  href?: string;
}
const ProfileViewLink = ({ leftIcon: Icon, title, rightElement, ...props }: ProfileViewLinkProps) => {
  return (
    <Flex
      width="100%"
      gap={4}
      py="14px"
      px={2}
      alignItems="center"
      cursor="pointer"
      layerStyle="clickable"
      borderRadius="button"
      {...props}
    >
      <Icon boxSize={5} />
      <Text flexGrow={1} textStyle="detail">
        {title}
      </Text>
      {rightElement}
    </Flex>
  );
};

export const ProfileView = ({ address, onClickLanguage, onOpenReferralModal, ...props }: ProfileViewProps) => {
  const { t } = useTranslation();
  const isBlastApp = useIsBlastApp();
  const { data: user } = useUser(address!, { enabled: !!address });
  const { data: userPoints } = useUserSeasonPoints(address!, {
    enabled: !!address,
  });

  const { locale = "en" } = useRouter();
  const handleDisconnect = useDisconnectWallet();

  const remainingLotteryTickets = userPoints?.lotteryTickets?.remaining
    ? formatCompactNumber(Number(userPoints.lotteryTickets.remaining))
    : null;

  return (
    <Stack
      width="360px"
      border="1px solid"
      borderColor="border-01"
      borderRadius="container"
      bg="ui-01"
      // @todo-yg update this value across the app when colors are final
      boxShadow="0px 16px 24px 0px #0000003D"
      p={4}
      spacing={4}
      {...props}
    >
      <Flex gap={4} width="100%" alignItems="center" as={NextLink} href="/settings">
        <ProfileAvatar
          src={user?.avatar?.image.src}
          address={address || zeroAddress}
          isPrivate={user?.isProfileImageVisible === false}
          size={48}
        />
        <Text bold>{formatAddress(address || zeroAddress, 6, 4)}</Text>
      </Flex>
      <Stack spacing={0} color="white">
        <Stack spacing={0} bg="ui-02" border="1px solid" borderColor="border-01" borderRadius="container" p={2}>
          {/* Rakeback */}
          <ProfileRakebackRow address={address} />

          {/* Lottery tickets */}
          <Flex p={2} alignItems="center" gap={4} width="100%">
            <TicketRedeemablesFillIcon boxSize={5} />
            <Text textStyle="detail" flexGrow={1}>
              {t("Lottery Tickets")}
            </Text>
            <Button size="xs" as={NextLink} href="/lottery" isDisabled={!remainingLotteryTickets}>
              {remainingLotteryTickets
                ? t("Use {{remainingTickets}} Tickets", { remainingTickets: remainingLotteryTickets })
                : t("Nothing To Claim")}
            </Button>
          </Flex>
        </Stack>

        <ProfileViewLink
          leftIcon={LevelsFilledIcon}
          title={t("Quest Progress")}
          rightElement={
            <Text textStyle="detail" color="text-03">
              {userPoints?.seasonPoints?.seasonPoints
                ? formatCompactNumber(Number(userPoints.seasonPoints.seasonPoints))
                : "0"}{" "}
              Pts
            </Text>
          }
          as={NextLink}
          href="/rewards"
        />

        <ProfileViewLink leftIcon={LinkIcon} title={t("Referral Link")} onClick={onOpenReferralModal} />

        <Divider borderColor="ui-bg" my={2} />

        <ProfileViewLink leftIcon={SettingsFillIcon} title={t("Profile Settings")} as={NextLink} href="/settings" />
        <ProfileViewLink
          leftIcon={GlobeIcon}
          title={t("Language")}
          onClick={onClickLanguage}
          rightElement={
            <Flex gap={4} alignItems="center">
              <Text textStyle="detail" color="text-03">
                {languageList[locale].short}
              </Text>
              <Chevron direction="right" boxSize={5} color="text-03" />
            </Flex>
          }
        />
        {!isBlastApp && (
          <>
            <Divider borderColor="ui-bg" my={2} />
            <ProfileViewLink leftIcon={LogoutIcon} title={t("Disconnect")} onClick={handleDisconnect} />
          </>
        )}
      </Stack>
    </Stack>
  );
};
